import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ApiError,
  getServer,
  listOrgs,
  me,
  request,
  type Customer,
  type OrgSummary,
} from "./api";
import { getToken } from "./auth";
import { useAuthScopeEpoch } from "../hooks/useAuthScopeEpoch";
import { workspaceFromRoute, workspaceHref } from "./workspacePaths";

export interface Workspace {
  id: string;
  org_id: string | null;
  project_id: string | null;
  project_name: string | null;
  project_permissions: string[];
  name: string;
  role: string;
  plan: string;
  plan_name: string;
  subscription_status: string;
  gate_state: string;
  server_count: number;
  monthly_tool_calls: number;
  limits: { servers: number | null; monthly_tool_calls: number | null };
  features: Record<
    string,
    {
      label: string;
      minimum_plan: string;
      included: boolean;
      reason: string;
    }
  >;
  permissions: {
    create_server: boolean;
    create_server_reason: string;
    edit_resources: boolean;
    manage_resources: boolean;
    manage_members: boolean;
    invite_members: boolean;
    manage_billing: boolean;
    manage_owners: boolean;
  };
}
interface WorkspaceState {
  customer: Customer | null;
  orgs: OrgSummary[];
  workspace: Workspace | null;
  loading: boolean;
  error: string;
  refresh: () => void;
  selectWorkspace: (id: string, path?: string) => void;
  href: (path: string) => string;
}
const Context = createContext<WorkspaceState | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const epoch = useAuthScopeEpoch();
  const location = useLocation();
  const navigate = useNavigate();
  const token = getToken();
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<{
    key: string;
    customer: Customer;
    orgs: OrgSummary[];
    workspace: Workspace;
  } | null>(null);
  const [failure, setFailure] = useState<{
    key: string;
    message: string;
  } | null>(null);
  const requestedProject = new URLSearchParams(location.search).get("project");
  const requested = workspaceFromRoute(location.pathname, location.search);
  const serverId = location.pathname.match(/^\/servers\/([^/]+)$/)?.[1];
  const resourceId = serverId && serverId !== "new" ? serverId : null;
  const key = `${epoch}:${requested ?? "resume"}:${resourceId ?? ""}:${requestedProject ?? ""}:${revision}`;

  useEffect(() => {
    if (!token) {
      setData(null);
      setFailure(null);
      return;
    }
    let stale = false;
    const controller = new AbortController();
    Promise.all([
      me(),
      listOrgs(),
      resourceId
        ? getServer(resourceId, controller.signal)
        : Promise.resolve(null),
    ])
      .then(async ([customer, orgs, server]) => {
        let selected = server ? server.org_id || "personal" : requested;
        if (!selected) {
          try {
            selected = sessionStorage.getItem(`pugio.workspace.${customer.id}`);
          } catch {
            /* optional navigation preference */
          }
        }
        // A stale explicit workspace is an error, never a personal fallback.
        if (
          selected &&
          selected !== "personal" &&
          !orgs.some((org) => org.id === selected)
        ) {
          throw new Error(
            "This workspace is no longer available to your account. Choose another workspace.",
          );
        }
        selected ||= orgs.length === 1 ? orgs[0].id : "personal";
        const actualProject = server ? server.project_id : requestedProject;
        const scopeQuery = new URLSearchParams();
        if (selected !== "personal") scopeQuery.set("org_id", selected);
        if (actualProject) scopeQuery.set("project_id", actualProject);
        const workspace = await request<Workspace>(`/workspace?${scopeQuery}`, {
          signal: controller.signal,
        });
        if (stale) return;
        try {
          sessionStorage.setItem(
            `pugio.workspace.${customer.id}`,
            workspace.id,
          );
        } catch {
          /* storage is optional */
        }
        setData({ key, customer, orgs, workspace });
        setFailure(null);
      })
      .catch((error) => {
        if (!stale)
          setFailure({
            key,
            message:
              error instanceof ApiError
                ? error.detail
                : error instanceof Error
                  ? error.message
                  : "Could not load this workspace.",
          });
      });
    return () => {
      stale = true;
      controller.abort();
    };
  }, [key, token, requested, resourceId, requestedProject]);

  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const selectWorkspace = useCallback(
    (id: string, path = "/servers") => navigate(workspaceHref(path, id)),
    [navigate],
  );
  const current = data?.key === key ? data : null;
  const error = failure?.key === key ? failure.message : "";
  const value = useMemo<WorkspaceState>(
    () => ({
      customer: current?.customer ?? null,
      orgs: current?.orgs ?? [],
      workspace: current?.workspace ?? null,
      loading: !!token && !current && !error,
      error,
      refresh,
      selectWorkspace,
      href: (path) =>
        current
          ? workspaceHref(
              path,
              current.workspace.id,
              current.workspace.project_id,
            )
          : path,
    }),
    [current, token, error, refresh, selectWorkspace],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useWorkspace(): WorkspaceState {
  const value = useContext(Context);
  if (!value) throw new Error("WorkspaceProvider is required");
  return value;
}

export function useResourceAccess(kind: string) {
  const { workspace } = useWorkspace();
  return {
    workspace,
    read:
      !!workspace &&
      (!workspace.project_id ||
        workspace.project_permissions.includes(`${kind}.read`)),
    edit:
      !!workspace &&
      (workspace.project_id
        ? workspace.project_permissions.includes(`${kind}.edit`)
        : workspace.permissions.edit_resources),
    manage:
      !!workspace &&
      (workspace.project_id
        ? workspace.project_permissions.includes(`${kind}.manage`)
        : workspace.permissions.manage_resources),
  };
}

/** Existing resource editors share the shell's scope; changing it remounts
 * the editor and drops drafts, selected resources and one-time credentials. */
export function useWorkspaceSelection(): [string, (orgId: string) => void] {
  const { workspace, selectWorkspace } = useWorkspace();
  const location = useLocation();
  return [
    workspace?.org_id ?? "",
    (orgId) => selectWorkspace(orgId || "personal", location.pathname),
  ];
}
